import api from '@/lib/api';
import type {
  InboxBotSummary,
  InboxConversationDetail,
  InboxConversationSummary,
  InboxPersonSummary,
} from '@workspace/types';

type ApiResponse<T> = { success: boolean; data: T; error?: string; code?: string };

export type InboxMessage = {
  id: string;
  sender: 'USER' | 'BOT' | 'OPERATOR';
  contentType: string;
  content: string;
  contextContent?: string | null;
  fileUrl: string | null;
  fileMetadata?: Record<string, unknown> | null;
  channel: string;
  createdAt: string;
};

export const inboxApi = {
  async listInboxes(tenantId?: string): Promise<InboxBotSummary[]> {
    const response = await api.get<ApiResponse<InboxBotSummary[]>>('/admin/inbox/inboxes', {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data.data;
  },

  async listConversations(params: {
    tenantId?: string;
    botId?: string;
    channel?: string;
    handoffStatus?: string;
    ticketStatus?: string;
    q?: string;
    cursor?: string;
  }): Promise<{ items: InboxConversationSummary[]; nextCursor: string | null }> {
    const response = await api.get<
      ApiResponse<{ items: InboxConversationSummary[]; nextCursor: string | null }>
    >('/admin/inbox/conversations', { params });
    return response.data.data;
  },

  async getConversation(
    conversationId: string,
    tenantId?: string,
  ): Promise<InboxConversationDetail> {
    const response = await api.get<ApiResponse<InboxConversationDetail>>(
      `/admin/inbox/conversations/${conversationId}`,
      { params: tenantId ? { tenantId } : undefined },
    );
    return response.data.data;
  },

  async listMessages(conversationId: string, tenantId?: string): Promise<InboxMessage[]> {
    const response = await api.get<ApiResponse<InboxMessage[]>>(
      `/admin/inbox/conversations/${conversationId}/messages`,
      { params: tenantId ? { tenantId } : undefined },
    );
    return response.data.data;
  },

  async reply(conversationId: string, content: string, tenantId?: string): Promise<void> {
    await api.post(
      `/admin/inbox/conversations/${conversationId}/messages`,
      { content },
      { params: tenantId ? { tenantId } : undefined },
    );
  },

  async replyWithFile(
    conversationId: string,
    file: File,
    content?: string,
    tenantId?: string,
  ): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    if (content?.trim()) {
      form.append('content', content.trim().slice(0, 4096));
    }
    await api.post(`/admin/inbox/conversations/${conversationId}/messages`, form, {
      params: tenantId ? { tenantId } : undefined,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  async resume(conversationId: string, tenantId?: string): Promise<void> {
    await api.post(
      `/admin/inbox/conversations/${conversationId}/resume`,
      {},
      { params: tenantId ? { tenantId } : undefined },
    );
  },

  async closeTicket(
    conversationId: string,
    tenantId?: string,
  ): Promise<{ number: number }> {
    const response = await api.post<ApiResponse<{ number: number }>>(
      `/admin/inbox/conversations/${conversationId}/tickets/close`,
      {},
      { params: tenantId ? { tenantId } : undefined },
    );
    return response.data.data;
  },

  async getPerson(personId: string, tenantId?: string): Promise<InboxPersonSummary> {
    const response = await api.get<ApiResponse<InboxPersonSummary>>(
      `/admin/inbox/people/${personId}`,
      { params: tenantId ? { tenantId } : undefined },
    );
    return response.data.data;
  },
};
